import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { 
  Calculator,
  Plus,
  ArrowRight,
  Search,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = API_BASE;

const CreditEvaluationList = () => {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchEvaluations();
  }, []);

  const fetchEvaluations = async () => {
    try {
      const response = await axios.get(`${API}/credit-evaluations`);
      setEvaluations(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvaluations = evaluations.filter(e =>
    e.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-evaluation-list-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <Calculator className="w-6 h-6 text-purple-600" />
            Asignar Riesgo
          </h1>
          <p className="text-muted-foreground">Evaluaciones de crédito y asignación de tier</p>
        </div>
        <Button onClick={() => navigate('/apps/credit-evaluation/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Evaluación
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar por cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredEvaluations.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvaluations.map((evaluation) => (
            <Card 
              key={evaluation.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/apps/credit-evaluation/${evaluation.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-12 h-12 rounded-lg flex items-center justify-center',
                      evaluation.assigned_tier ? 'bg-purple-100' : 'bg-red-100'
                    )}>
                      <Calculator className={cn(
                        'w-6 h-6',
                        evaluation.assigned_tier ? 'text-purple-600' : 'text-red-600'
                      )} />
                    </div>
                    <div>
                      <CardTitle className="text-base">Evaluación de Crédito</CardTitle>
                      <CardDescription>{evaluation.client_name || 'Sin cliente'}</CardDescription>
                    </div>
                  </div>
                  {evaluation.assigned_tier ? (
                    <Badge variant="default">
                      Tier {evaluation.assigned_tier}
                      {evaluation.tier_override && '*'}
                    </Badge>
                  ) : (
                    <Badge variant="destructive">Sin Tier</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Precio Equipo</p>
                    <p className="font-medium">${evaluation.equipment_price?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Tiers Elegibles</p>
                    <p className="font-medium">{evaluation.eligible_tiers?.length || 0}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-primary font-medium pt-2">
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <Calculator className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No hay evaluaciones</h3>
            <p className="text-muted-foreground mb-4">Comienza creando tu primera evaluación de crédito</p>
            <Button onClick={() => navigate('/apps/credit-evaluation/new')}>
              <Plus className="w-4 h-4 mr-2" /> Nueva Evaluación
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CreditEvaluationList;
